import { Fingerprint, Cookie, ShieldCheck } from '@phosphor-icons/react';
import { cn } from '@concord-platform/ui/cn';

export type FloatingButtonProps = {
  className?: string;
  hide: boolean;
  icon: 'cookie' | 'fingerprint' | 'shield';
  position: 'left' | 'right';
  tabText: string;
  theme: 'light' | 'dark' | 'brand';
  toggleOpen: () => void;
  type: 'tab' | 'circle';
  zIndex?: number;
};

export function FloatingButtonUI({
  className,
  hide,
  icon,
  position,
  tabText,
  theme,
  toggleOpen,
  type,
  zIndex,
}: FloatingButtonProps) {
  const display = hide ? 'hidden' : 'inline-block';
  const iconComponents = {
    cookie: <Cookie size={32} />,
    fingerprint: <Fingerprint size={32} />,
    shield: <ShieldCheck size={32} />,
  };

  const iconComponent = iconComponents[icon];

  const baseClasses = cn(
    'bg-background text-foreground border border-transparent shadow-lg cursor-pointer text-sm font-medium fixed',
    display,
  );

  const themeClasses = {
    light: 'light hover:text-primary-foreground hover:bg-primary',
    dark: 'dark hover:text-primary-foreground hover:bg-primary',
    brand:
      'bg-primary text-primary-foreground border-primary hover:bg-primary-hover',
  };

  let positionClass;
  if (position === 'left') {
    positionClass = type === 'tab' ? 'left-0' : 'left-circle-offset-x';
  } else {
    positionClass = type === 'tab' ? 'right-0' : 'right-circle-offset-x';
  }

  const tabClasses = cn(
    'py-2 px-4 border-none rounded-t-xl bottom-tab-offset-y',
    position === 'left'
      ? 'rotate-90 origin-bottom-left'
      : '-rotate-90 origin-bottom-right',
  );

  const circleClasses = `p-2 rounded-full bottom-circle-offset-y hover:scale-110`;

  return (
    <button
      aria-controls={'concord-widget'}
      aria-expanded={hide}
      className={cn(
        baseClasses,
        positionClass,
        themeClasses[theme],
        type === 'tab' ? tabClasses : circleClasses,
        className,
      )}
      data-testid="cd-floating-button"
      style={{ zIndex }}
      onClick={() => {
        toggleOpen();
        document.getElementById('concord-widget')?.focus();
      }}
    >
      {type === 'tab' ? tabText : iconComponent}
    </button>
  );
}
